import { Injectable, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@app/services/auth/auth/auth.service';
import type {
  PublicAuthResponse,
  PublicLoginPayload,
  PublicUser,
  User,
} from '@app/interfaces/auth/auth.interfaces';
import { isStrongPassword as sharedIsStrongPassword } from '@app/validators/password/password.validators';

export type {
  PublicAuthResponse,
  PublicLoginPayload,
  PublicUser,
} from '@app/interfaces/auth/auth.interfaces';

@Injectable({
  providedIn: 'root',
})
export class PublicAuthService {
  private readonly authService = inject(AuthService);

  readonly user = computed(() => this.getMappedAuthenticatedUser());
  readonly account = computed(() => this.authService.user());
  readonly effectiveUser = this.user;
  readonly isLoggedIn = computed(() => this.effectiveUser() !== null);

  get redirectPath(): string {
    return this.authService.redirectPath;
  }

  hasActiveSession(): boolean {
    return this.authService.hasUser();
  }

  persistServerSession(response: PublicAuthResponse): PublicUser | null {
    if (!response.user) {
      return null;
    }

    this.authService.saveLogin(response);

    return this.getMappedAuthenticatedUser();
  }

  async login(payload: PublicLoginPayload): Promise<'admin' | 'public'> {
    const response = await firstValueFrom(this.authService.login(payload));
    this.authService.saveLogin(response);
    const role = response.user?.role;

    return role === 'super_admin' || role === 'admin' ? 'admin' : 'public';
  }

  async requestPasswordReset(email: string): Promise<string> {
    const response = await firstValueFrom(
      this.authService.requestPasswordReset({ email: email.trim().toLowerCase() }),
    );

    return response.message;
  }

  async verifyPasswordReset(email: string, otp: string): Promise<string> {
    const response = await firstValueFrom(
      this.authService.verifyPasswordReset({
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
      }),
    );

    return response.reset_token;
  }

  async verifyPasswordResetLink(email: string, resetToken: string): Promise<string> {
    const response = await firstValueFrom(
      this.authService.verifyPasswordResetLink({
        email: email.trim().toLowerCase(),
        reset_token: resetToken.trim(),
      }),
    );

    return response.email;
  }

  async completePasswordReset(
    email: string,
    resetToken: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<string> {
    const response = await firstValueFrom(
      this.authService.completePasswordReset({
        email: email.trim().toLowerCase(),
        reset_token: resetToken.trim(),
        password,
        password_confirmation: passwordConfirmation,
      }),
    );

    return response.message;
  }

  logout(): void {
    this.authService.logout().subscribe({ error: () => undefined });
    this.authService.clearSession();
  }

  async restoreSessionFromServer(): Promise<void> {
    if (!this.authService.getAuthToken()) {
      this.authService.clearSession();
      return;
    }

    try {
      const response = await firstValueFrom(this.authService.validate());
      if (!this.authService.saveValidatedSession(response)) {
        this.authService.clearSession();
      }
    } catch {
      this.authService.clearSession();
    }
  }

  isEmailTaken(email: string): boolean {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return false;

    const user = this.effectiveUser();
    if (!user) return false;

    return user.email === normalized;
  }

  isStrongPassword(password: string): boolean {
    return sharedIsStrongPassword(password.trim());
  }

  private getMappedAuthenticatedUser(): PublicUser | null {
    const currentUser = this.authService.user();
    if (!currentUser) {
      return null;
    }

    return {
      uuid: currentUser.uuid,
      fullName: String(currentUser.name ?? '').trim(),
      email: String(currentUser.email ?? '')
        .trim()
        .toLowerCase(),
      hasPassword: true,
      wantsUpdates: false,
      visitPurpose: this.visitPurposeForUser(currentUser),
      visitDate: '',
      specialization: '',
      accountStatus: this.accountStatusForUser(currentUser),
    };
  }

  private visitPurposeForUser(user: User): string {
    if (user.role === 'super_admin') {
      return 'Super Admin CMS';
    }

    if (user.role === 'admin') {
      return 'Admin CMS';
    }

    return 'Public Portal';
  }

  private accountStatusForUser(user: User): PublicUser['accountStatus'] {
    if (user.role === 'super_admin') {
      return 'super_admin';
    }

    if (user.role === 'admin') {
      return 'admin';
    }

    return 'normal_user';
  }
}
